import { FC, SVGProps, useState } from 'react'
import { Theme, useTheme } from '@mui/material'
import ZButton from '../components/ZButton'
import {
  CategorisationTreeOption,
  CategorisationTreeQuestion,
  SubmissionAnswer,
} from '../types'
import TreeQuestion from '../components/TreeQuestion'
import ZCheckbox from '../components/ZCheckbox'
import ZIcon from '../components/ZIcon'
import ZText from '../components/ZText'
import ZBottomContainer from '../components/ZBottomContainer'
import ZScreenContainer from '../components/ZScreenContainer'
import Header from '../components/Header'
import {
  AnswerMap,
  collectAnswers,
  hasNoneIntent,
  isComplete,
  pruneAnswers,
} from '../util/categorisationTree'
import {
  RestrictedInvestorIcon,
  HighNetWorthInvestorIcon,
  CertifiedSophisticatedInvestorIcon,
} from '../assets/icons'

const categoryIcons: Record<string, FC<SVGProps<SVGSVGElement>>> = {
  RESTRICTED_INVESTOR: RestrictedInvestorIcon,
  HIGH_NET_WORTH_INVESTOR: HighNetWorthInvestorIcon,
  CERTIFIED_SOPHISTICATED_INVESTOR: CertifiedSophisticatedInvestorIcon,
}

const DECLARATION_TOPICS = [
  'HNW_DECLARATION',
  'CSI_ACCEPTANCE_DECLARATION',
  'RESTRICTED_DECLARATION',
]

const QUESTION_TAGS: Record<string, string> = {
  RESTRICTED_PAST_12_MONTHS: 'A',
  RESTRICTED_NEXT_12_MONTHS: 'B',
}

const isDeclaration = (question: CategorisationTreeQuestion) =>
  DECLARATION_TOPICS.includes(question.topic)

const CategorisationForm = ({
  rootQuestion,
  selectedOption,
  onComplete,
  recategorise,
  onLeave,
  loading,
}: {
  rootQuestion: CategorisationTreeQuestion
  selectedOption: CategorisationTreeOption
  onComplete: (answers: SubmissionAnswer[], signedAt: string) => void
  recategorise: () => void
  onLeave: () => void
  loading: boolean
}) => {
  const theme = useTheme<Theme>()

  const [answers, setAnswers] = useState<AnswerMap>({
    [rootQuestion.id]: { answerId: selectedOption.id },
  })

  // signedAt = the moment the customer ticked the declaration ("I accept …").
  const [declarationSignedAt, setDeclarationSignedAt] = useState<string>()

  const [confirmingExit, setConfirmingExit] = useState(false)

  const subtree = selectedOption.nextQuestions ?? []
  const boxQuestions = subtree.filter(q => !isDeclaration(q))
  const declarationQuestion = subtree.find(isDeclaration)
  const declarationYesOption = declarationQuestion?.options?.find(
    option => option.value === 'YES',
  )

  const declarationAccepted =
    !!declarationQuestion &&
    answers[declarationQuestion.id]?.answerId === declarationYesOption?.id

  const handleChange = (
    questionId: string,
    answer: { answerId?: string; answerInput?: string },
  ) => {
    setAnswers(previous =>
      pruneAnswers([rootQuestion], { ...previous, [questionId]: answer }),
    )
  }

  // Completeness only — every visible question (incl. declaration) answered.
  // No correctness checks: "unacceptable" inputs must be submittable (Phase 1).
  const formComplete = isComplete([rootQuestion], answers)

  const noneIntent = hasNoneIntent([rootQuestion], answers)

  const CategoryIcon = categoryIcons[selectedOption.value]

  if (confirmingExit) {
    return (
      <ZScreenContainer>
        <Header
          title={selectedOption.label}
          onBack={() => setConfirmingExit(false)}
        />

        {CategoryIcon ? (
          <ZIcon
            Icon={CategoryIcon}
            title="categorisation icon"
            style={{ alignSelf: 'center' }}
          />
        ) : null}

        <ZText>
          {
            'What would you like to do?\nPlease select one of the options below to continue.'
          }
        </ZText>

        <ZBottomContainer>
          <ZButton disabled={loading} onClick={() => recategorise()}>
            Choose a different category
          </ZButton>

          <ZButton disabled={loading} onClick={() => onLeave()}>
            I wish to leave this investment journey
          </ZButton>
        </ZBottomContainer>
      </ZScreenContainer>
    )
  }

  return (
    <ZScreenContainer>
      <Header title={selectedOption.label} onBack={() => recategorise()} />

      {CategoryIcon ? (
        <ZIcon
          Icon={CategoryIcon}
          title="categorisation icon"
          style={{ alignSelf: 'center' }}
        />
      ) : null}

      {selectedOption.statement ? (
        <ZText>{selectedOption.statement}</ZText>
      ) : null}

      {boxQuestions.length > 0 && (
        <div
          style={{
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
            borderStyle: 'solid',
            padding: '1.5rem',
            rowGap: '1rem',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '430px',
            boxSizing: 'border-box',
          }}
        >
          {selectedOption.preQuestionStatement ? (
            <ZText>{selectedOption.preQuestionStatement}</ZText>
          ) : null}

          {boxQuestions.map(question => (
            <TreeQuestion
              key={question.id}
              question={question}
              answers={answers}
              onChange={handleChange}
              tag={QUESTION_TAGS[question.topic]}
            />
          ))}
        </div>
      )}

      {boxQuestions.length === 0 && selectedOption.preQuestionStatement ? (
        <ZText>{selectedOption.preQuestionStatement}</ZText>
      ) : null}

      <ZBottomContainer>
        {declarationQuestion && declarationYesOption ? (
          <ZCheckbox
            label={declarationQuestion.label}
            onChange={event => {
              if (event.target.checked) {
                setDeclarationSignedAt(new Date().toISOString())
              }
              handleChange(declarationQuestion.id, {
                answerId: event.target.checked
                  ? declarationYesOption.id
                  : undefined,
              })
            }}
            checked={declarationAccepted}
            // Phase 1 req: declaration checkbox is ALWAYS enabled — never
            // disable it based on the other answers' validity or presence.
            disabled={false}
          />
        ) : null}

        <ZButton
          style={{ marginTop: '1rem' }}
          loading={loading}
          onClick={() =>
            onComplete(
              collectAnswers([rootQuestion], answers),
              // The button is only enabled once the declaration is ticked, so
              // declarationSignedAt is set; fall back to now defensively.
              declarationSignedAt ?? new Date().toISOString(),
            )
          }
          disabled={!formComplete}
        >
          Next
        </ZButton>

        <ZButton
          disabled={loading}
          onClick={() =>
            noneIntent ? setConfirmingExit(true) : recategorise()
          }
        >
          {noneIntent
            ? 'Choose a different category / leave this journey'
            : 'Choose a different category'}
        </ZButton>
      </ZBottomContainer>
    </ZScreenContainer>
  )
}

export default CategorisationForm
